import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ThemePicker.css';

/**
 * Theme picker.
 *
 * Rebuilt around three things the old one could not do:
 *
 *  - It is keyboard operable. The cards used to be <div onClick>, which meant
 *    they could not be reached by Tab at all and the whole picker was
 *    unusable without a mouse. They are buttons now, with arrow keys moving
 *    across the grid.
 *  - Categories are a filter row rather than headings stacked between rows of
 *    cards, so the grid stays one continuous shelf and the list can be cut
 *    down instead of scrolled through.
 *  - Hovering a card previews the theme on the page behind, and on the picker
 *    itself. Nothing is committed until you click, and leaving the card puts
 *    the old theme straight back.
 */

const ALL = 'All';

const ThemePicker = ({ themes, currentTheme, onSelect, onClose }) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ALL);
  const searchRef = useRef(null);
  const gridRef = useRef(null);

  const categories = useMemo(() => {
    const seen = [];
    themes.forEach((t) => {
      if (!seen.includes(t.category)) seen.push(t.category);
    });
    return [ALL, ...seen];
  }, [themes]);

  const counts = useMemo(() => {
    const map = { [ALL]: themes.length };
    themes.forEach((t) => {
      map[t.category] = (map[t.category] || 0) + 1;
    });
    return map;
  }, [themes]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return themes.filter((t) => {
      if (category !== ALL && t.category !== category) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [themes, query, category]);

  /**
   * Live preview.
   *
   * Only the data-theme attribute is touched, never the app's theme state —
   * so this repaints through CSS without React unmounting and remounting the
   * whole presentation on every hover. Committing a theme still goes through
   * onSelect, which sets both.
   */
  const previewing = useRef(false);

  const restore = useCallback(() => {
    if (!previewing.current) return;
    previewing.current = false;
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  const preview = useCallback((id) => {
    if (id === currentTheme) return;
    previewing.current = true;
    document.documentElement.setAttribute('data-theme', id);
  }, [currentTheme]);

  // The real theme goes back on when the picker unmounts.
  useEffect(() => restore, [restore]);

  /**
   * ...and whenever the grid changes under the cursor.
   *
   * Filtering or searching can unmount the card being hovered, and an element
   * that is removed never fires pointerleave — so without this the preview
   * would stick and the site would be left showing a theme the user only
   * hovered over.
   */
  useEffect(() => {
    restore();
  }, [shown, restore]);

  // Escape closes; the page behind stops scrolling while this is open.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        restore();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    searchRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, restore]);

  /** Arrow keys walk the grid; the row width is read from the live layout. */
  const onGridKeyDown = useCallback((event) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'];
    if (!keys.includes(event.key)) return;

    const cards = Array.from(gridRef.current?.querySelectorAll('.tp__card') || []);
    const index = cards.indexOf(document.activeElement);
    if (index === -1) return;

    event.preventDefault();

    // Cards on the same visual row share a top offset.
    const firstTop = cards[0].offsetTop;
    const perRow = cards.filter((c) => c.offsetTop === firstTop).length || 1;

    const step = {
      ArrowRight: 1,
      ArrowLeft: -1,
      ArrowDown: perRow,
      ArrowUp: -perRow,
    }[event.key];

    const next = cards[Math.min(cards.length - 1, Math.max(0, index + step))];
    next?.focus();
  }, []);

  const choose = useCallback(
    (id) => {
      previewing.current = false;
      onSelect(id);
    },
    [onSelect]
  );

  return (
    <div
      className="tp"
      role="presentation"
      onClick={() => {
        restore();
        onClose();
      }}
    >
      <div
        className="tp__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tp-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tp__head">
          <div className="tp__headings">
            <p className="tp__eyebrow">Appearance</p>
            <h2 className="tp__title" id="tp-title">
              Themes
              <i className="fa-solid fa-wand-magic-sparkles tp__spark" aria-hidden="true" />
            </h2>
          </div>

          <div className="tp__search">
            <i className="fas fa-magnifying-glass tp__search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              className="tp__search-input"
              placeholder="Search themes..."
              aria-label="Search themes"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="tp__search-clear"
                aria-label="Clear search"
                onClick={() => {
                  setQuery('');
                  searchRef.current?.focus();
                }}
              >
                &times;
              </button>
            )}
          </div>

          <button
            type="button"
            className="tp__close"
            aria-label="Close theme picker"
            onClick={() => {
              restore();
              onClose();
            }}
          >
            &times;
          </button>
        </header>

        <div className="tp__filters" role="group" aria-label="Filter by category">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`tp__filter${category === c ? ' is-active' : ''}`}
              aria-pressed={category === c}
              onClick={() => setCategory(c)}
            >
              {c}
              <span className="tp__filter-count">{counts[c] || 0}</span>
            </button>
          ))}
        </div>

        <div
          className="tp__grid"
          ref={gridRef}
          onKeyDown={onGridKeyDown}
          onMouseLeave={restore}
        >
          {shown.map((theme) => {
            const active = theme.id === currentTheme;
            return (
              <button
                key={theme.id}
                type="button"
                className={`tp__card${active ? ' is-active' : ''}`}
                data-theme={theme.id}
                aria-pressed={active}
                onClick={() => choose(theme.id)}
                onPointerEnter={() => preview(theme.id)}
                onFocus={() => preview(theme.id)}
                onPointerLeave={restore}
                onBlur={restore}
              >
                {/* The scene, and a wash over it so the caption stays legible
                    on the light themes as well as the dark ones.

                    Loaded eagerly: the whole set is ~128 kB and only fetched
                    when the picker is opened, so deferring it would just mean
                    the grid paints as twelve empty rectangles first. */}
                <img
                  className="tp__art"
                  src={theme.card}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  width="800"
                  height="500"
                />
                <span className="tp__wash" aria-hidden="true" />

                <span className="tp__badge" aria-hidden="true">
                  <i className={theme.icon} />
                </span>

                {/* Decoration, not a control — there is no card menu to open.
                    Marked hidden so it is not announced as one. */}
                <span className="tp__dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>

                <span className="tp__body">
                  <span className="tp__name">{theme.name}</span>
                  <span className="tp__desc">{theme.description}</span>
                  <span className="tp__state">
                    {active && <i className="fa-solid fa-check" aria-hidden="true" />}
                    {active ? 'Active' : 'Apply'}
                  </span>
                </span>
              </button>
            );
          })}

          {shown.length === 0 && (
            <p className="tp__empty">
              Nothing matches &ldquo;{query}&rdquo;.
            </p>
          )}
        </div>

        <footer className="tp__foot">
          <span aria-live="polite">
            {shown.length} of {themes.length} themes
          </span>
          <span className="tp__hint">
            <kbd>Esc</kbd>
            to close
          </span>
        </footer>
      </div>
    </div>
  );
};

export default ThemePicker;
